import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "../components/Brand";
import { adminApi, API_BASE_URL } from "../lib/api";
import { useAdminSession, type AdminAuthSession } from "../lib/session";
import { ApiError, EvochaClient } from "@evocha/api-client";

type LoginResult = AdminAuthSession & { needs_phone?: boolean };

function clientFor(token: string): EvochaClient {
  return new EvochaClient({
    baseUrl: API_BASE_URL,
    getToken: () => token,
    headers: () => ({ "X-Client": "evocha-admin/1.0.0" }),
  });
}

function deviceId(): string {
  const key = "evocha.admin.device";
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const created = typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `dev_${Date.now().toString(36)}`;
    localStorage.setItem(key, created);
    return created;
  } catch {
    return "unknown-device";
  }
}

function problemMessage(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong. Try again.";
}

export function Login() {
  const [step, setStep] = useState<"email" | "otp" | "pin" | "phone">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [pin, setPin] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [deviceChallenge, setDeviceChallenge] = useState("");
  const [pendingSession, setPendingSession] = useState<LoginResult | null>(null);
  const [phoneStage, setPhoneStage] = useState<"number" | "code">("number");
  const [phone, setPhone] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneChallenge, setPhoneChallenge] = useState("");
  const [phoneMasked, setPhoneMasked] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const { signIn } = useAdminSession();
  const navigate = useNavigate();

  const requestCode = useCallback(async () => {
    if (!email.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.post<{ challenge_id: string }>("/admin/v1/auth/otp/request", {
        email: email.trim(),
      });
      setChallengeId(result.challenge_id);
      setStep("otp");
    } catch (err) {
      setError(problemMessage(err));
    } finally {
      setLoading(false);
    }
  }, [email, loading]);

  const verifyCode = useCallback(async () => {
    if (otp.length < 6 || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.post<{ next: string; device_challenge: string }>(
        "/admin/v1/auth/otp/verify",
        { challenge_id: challengeId, code: otp },
      );
      setDeviceChallenge(result.device_challenge);
      setStep("pin");
    } catch (err) {
      setError(problemMessage(err));
    } finally {
      setLoading(false);
    }
  }, [otp, challengeId, loading]);

  const signInWithPin = useCallback(async () => {
    if (pin.length < 4 || loading) return;
    setLoading(true);
    setError(null);
    try {
      const session = await adminApi.post<LoginResult>("/admin/v1/auth/pin", {
        device_challenge: deviceChallenge,
        pin,
        device_id: deviceId(),
      });
      if (session.needs_phone) {
        setPendingSession(session);
        setPhoneStage("number");
        setPhone("");
        setPhoneCode("");
        setStep("phone");
      } else {
        signIn(session);
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(problemMessage(err));
    } finally {
      setLoading(false);
    }
  }, [pin, deviceChallenge, loading, signIn, navigate]);

  const finishLogin = useCallback(() => {
    if (!pendingSession) return;
    signIn(pendingSession);
    navigate("/", { replace: true });
  }, [pendingSession, signIn, navigate]);

  const requestPhoneCode = useCallback(async () => {
    if (!pendingSession || loading) return;
    setLoading(true);
    setError(null);
    setResendNote(null);
    try {
      const result = await clientFor(pendingSession.access_token).post<{
        challenge_id: string;
        phone: string;
      }>("/admin/v1/auth/phone/request", { phone: phone.trim() });
      setPhoneChallenge(result.challenge_id);
      setPhoneMasked(result.phone);
      setPhoneStage("code");
      setPhoneCode("");
    } catch (err) {
      setError(problemMessage(err));
    } finally {
      setLoading(false);
    }
  }, [pendingSession, phone, loading]);

  const verifyPhoneCode = useCallback(async () => {
    if (!pendingSession || phoneCode.length < 6 || loading) return;
    setLoading(true);
    setError(null);
    try {
      await clientFor(pendingSession.access_token).post(
        "/admin/v1/auth/phone/verify",
        { challenge_id: phoneChallenge, code: phoneCode },
      );
      finishLogin();
    } catch (err) {
      setError(problemMessage(err));
    } finally {
      setLoading(false);
    }
  }, [pendingSession, phoneCode, phoneChallenge, loading, finishLogin]);

  const stepTitle = useMemo(() => {
    switch (step) {
      case "email":
        return "Sign in to admin";
      case "otp":
        return "Verify your identity";
      case "pin":
        return "Enter your PIN";
      case "phone":
        return phoneStage === "code" ? "Verify your number" : "Add your mobile number";
      default:
        return "";
    }
  }, [step, phoneStage]);

  const stepDesc = useMemo(() => {
    switch (step) {
      case "email":
        return "Use your admin credentials to sign in.";
      case "otp":
        return `We sent a 6-digit code to ${email.replace(/(.{2}).*@/, "$1***@…")}`;
      case "pin":
        return "Enter your 4–6 digit security PIN.";
      case "phone":
        return phoneStage === "code"
          ? `Enter the 6-digit code sent to ${phoneMasked}.`
          : "Add the mobile number that should receive your future sign-in codes.";
      default:
        return "";
    }
  }, [step, email, phoneStage, phoneMasked]);

  const errorBlock = error ? (
    <p className="admin-login-error" role="alert" style={{ color: "var(--ev-red-600)", fontSize: 13, marginTop: 10 }}>
      {error}
    </p>
  ) : null;

  // ── Email step ──
  if (step === "email") {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <div className="admin-login-brand">
            <Brand />
          </div>
          <h2 className="admin-login-title">Sign in to admin</h2>
          <p className="admin-login-desc">Use your admin email. We send a one-time code before asking for your PIN.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void requestCode();
            }}
          >
            <div className="admin-field">
              <label htmlFor="admin-email">Email address</label>
              <input
                id="admin-email"
                type="email"
                placeholder="name@skttanzania.co.tz"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            {errorBlock}
            <button
              type="submit"
              className="ev-btn ev-btn-primary ev-btn-lg"
              disabled={loading || !email.trim()}
            >
              {loading ? "Sending code…" : "Continue"}
            </button>
          </form>
          <p className="admin-login-footer">
            Protected by MFA · <a href="#">Forgot credentials?</a>
          </p>
        </div>
      </div>
    );
  }

  // ── OTP step ──
  if (step === "otp") {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <div className="admin-login-brand">
            <Brand />
          </div>
          <h2 className="admin-login-title">{stepTitle}</h2>
          <p className="admin-login-desc">{stepDesc}</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void verifyCode();
            }}
          >
            <div className="admin-field">
              <label htmlFor="admin-otp">Verification code</label>
              <input
                id="admin-otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                autoFocus
              />
            </div>
            {errorBlock}
            <button
              type="submit"
              className="ev-btn ev-btn-primary ev-btn-lg"
              disabled={loading || otp.length < 6}
            >
              {loading ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              className="ev-btn ev-btn-ghost ev-btn-sm"
              style={{ marginTop: 8 }}
              onClick={() => {
                setResendNote("A new code was requested.");
                void requestCode();
              }}
            >
              Resend code
            </button>
            {resendNote && <p className="ev-body-micro" style={{ marginTop: 6 }}>{resendNote}</p>}
          </form>
          <button
            className="ev-btn ev-btn-ghost ev-btn-link"
            onClick={() => { setStep("email"); setError(null); }}
          >
            ← Change email
          </button>
        </div>
      </div>
    );
  }

  // ── Phone enrolment step ──
  if (step === "phone") {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <div className="admin-login-brand">
            <Brand />
          </div>
          <h2 className="admin-login-title">{stepTitle}</h2>
          <p className="admin-login-desc">{stepDesc}</p>
          {phoneStage === "number" ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void requestPhoneCode();
              }}
            >
              <div className="admin-field">
                <label htmlFor="admin-phone">Mobile number</label>
                <input
                  id="admin-phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="0712 345 678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  autoFocus
                  autoComplete="tel"
                />
              </div>
              {errorBlock}
              <button
                type="submit"
                className="ev-btn ev-btn-primary ev-btn-lg"
                disabled={loading || phone.replace(/\D/g, "").length < 9}
              >
                {loading ? "Sending code…" : "Send code"}
              </button>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void verifyPhoneCode();
              }}
            >
              <div className="admin-field">
                <label htmlFor="admin-phone-code">Verification code</label>
                <input
                  id="admin-phone-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  value={phoneCode}
                  onChange={(e) =>
                    setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  required
                  autoFocus
                />
              </div>
              {errorBlock}
              <button
                type="submit"
                className="ev-btn ev-btn-primary ev-btn-lg"
                disabled={loading || phoneCode.length < 6}
              >
                {loading ? "Verifying…" : "Verify and continue"}
              </button>
              <button
                type="button"
                className="ev-btn ev-btn-ghost ev-btn-sm"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setResendNote("A new code was requested.");
                  void requestPhoneCode();
                }}
              >
                Resend code
              </button>
              {resendNote && (
                <p className="ev-body-micro" style={{ marginTop: 6 }}>
                  {resendNote}
                </p>
              )}
              <button
                type="button"
                className="ev-btn ev-btn-ghost ev-btn-link"
                onClick={() => {
                  setPhoneStage("number");
                  setError(null);
                }}
              >
                ← Change number
              </button>
            </form>
          )}
          <button
            type="button"
            className="ev-btn ev-btn-ghost ev-btn-link"
            onClick={finishLogin}
            disabled={loading}
          >
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  // ── PIN step ──
  return (
    <div className="admin-login">
      <div className="admin-login-card">
        <div className="admin-login-brand">
          <Brand />
        </div>
        <h2 className="admin-login-title">{stepTitle}</h2>
        <p className="admin-login-desc">{stepDesc}</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void signInWithPin();
          }}
        >
          <div className="admin-field">
            <label htmlFor="admin-pin">Security PIN</label>
            <input
              id="admin-pin"
              type="password"
              inputMode="numeric"
              maxLength={6}
              placeholder="Enter your PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
              autoFocus
            />
          </div>
          {errorBlock}
          <button
            type="submit"
            className="ev-btn ev-btn-primary ev-btn-lg"
            disabled={loading || pin.length < 4}
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <button
          className="ev-btn ev-btn-ghost ev-btn-link"
          onClick={() => { setStep("otp"); setError(null); }}
        >
          ← Back
        </button>
      </div>
    </div>
  );
}
